import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

// ── Types ─────────────────────────────────────────────────────────────────────

interface VariantInput {
  name: string;
  script: string;
  weight: number;
}

interface ExperimentVariant {
  id: string;
  name: string;
  script: string;
  weight: number;
  calls: number;
  answers: number;
  conversions: number;
  avgDuration: number;
  optOuts: number;
  answerRate: number;
  conversionRate: number;
  improvement: number;
  pValue: number;
  significant: boolean;
}

interface ExperimentResults {
  variants: ExperimentVariant[];
  winner: string | null;
  confidence: number;
  recommendation: string;
}

interface Experiment {
  id: string;
  name: string;
  status: 'running' | 'paused' | 'concluded';
  metric: string;
  minSampleSize: number;
  campaignId?: string;
  createdAt: string;
  variant_count?: number;
  total_calls?: number;
  variants?: ExperimentVariant[];
}

interface Campaign {
  id: string;
  name: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const statusColor: Record<string, string> = {
  running: '#10b981',
  paused: '#f59e0b',
  concluded: '#6b7280',
};

const statusBg: Record<string, string> = {
  running: 'rgba(16,185,129,0.1)',
  paused: 'rgba(245,158,11,0.1)',
  concluded: 'rgba(107,114,128,0.1)',
};

function confidenceColor(conf: number): string {
  if (conf >= 95) return '#10b981';
  if (conf >= 80) return '#f59e0b';
  return '#6b7280';
}

function metricLabel(m: string): string {
  return { answer_rate: 'Answer Rate', conversion_rate: 'Conversion Rate', call_duration: 'Call Duration', opt_out_rate: 'Opt-out Rate' }[m] || m;
}

function exportCSV(results: ExperimentResults, name: string) {
  const headers = ['Variant', 'Calls', 'Answers', 'Answer Rate', 'Conversions', 'Conv. Rate', 'Avg Duration', 'Improvement', 'p-value', 'Significant'];
  const rows = results.variants.map(v => [
    v.name, v.calls, v.answers, `${v.answerRate}%`, v.conversions, `${v.conversionRate}%`,
    `${v.avgDuration}s`, `${v.improvement > 0 ? '+' : ''}${v.improvement}%`, v.pValue, v.significant ? 'Yes' : 'No'
  ]);
  const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name.replace(/\s+/g, '_')}_results.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── New Experiment Modal ───────────────────────────────────────────────────────

interface NewExperimentModalProps {
  campaigns: Campaign[];
  onClose: () => void;
  onCreated: () => void;
}

const NewExperimentModal: React.FC<NewExperimentModalProps> = ({ campaigns, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [metric, setMetric] = useState('conversion_rate');
  const [minSampleSize, setMinSampleSize] = useState(100);
  const [variants, setVariants] = useState<VariantInput[]>([
    { name: 'Control', script: '', weight: 50 },
    { name: 'Variant A', script: '', weight: 50 },
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const rebalanceWeights = (newVariants: VariantInput[]) => {
    const equal = Math.floor(100 / newVariants.length);
    const rem = 100 - equal * newVariants.length;
    return newVariants.map((v, i) => ({ ...v, weight: equal + (i === 0 ? rem : 0) }));
  };

  const addVariant = () => {
    if (variants.length >= 4) return;
    const newVars = [...variants, { name: `Variant ${String.fromCharCode(64 + variants.length)}`, script: '', weight: 0 }];
    setVariants(rebalanceWeights(newVars));
  };

  const removeVariant = (idx: number) => {
    if (variants.length <= 2) return;
    const newVars = variants.filter((_, i) => i !== idx);
    setVariants(rebalanceWeights(newVars));
  };

  const updateVariant = (idx: number, field: keyof VariantInput, value: string | number) => {
    setVariants(prev => prev.map((v, i) => i === idx ? { ...v, [field]: value } : v));
  };

  const handleWeightChange = (idx: number, val: number) => {
    const others = variants.length - 1;
    const rest = 100 - val;
    const perOther = Math.floor(rest / others);
    const rem = rest - perOther * others;
    const newVars = variants.map((v, i) => {
      if (i === idx) return { ...v, weight: val };
      const isLast = i === variants.length - 1 && idx !== i;
      return { ...v, weight: perOther + (isLast ? rem : 0) };
    });
    setVariants(newVars);
  };

  const handleSubmit = async () => {
    if (!name.trim()) return setError('Name is required');
    if (variants.some(v => !v.script.trim())) return setError('All variants need a script');
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/api/v1/experiments`, {
        name,
        campaignId: campaignId || undefined,
        variants,
        metric,
        minSampleSize,
      }, { withCredentials: true });
      onCreated();
      onClose();
    } catch (e: any) {
      setError(e.response?.data?.error?.message || 'Failed to create experiment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div
        className="rounded-2xl overflow-hidden flex flex-col"
        style={{ background: '#13131e', border: '1px solid rgba(255,255,255,0.08)', width: '90%', maxWidth: 720, maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <h2 className="text-white font-semibold text-lg">🧪 New A/B Experiment</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">✕</button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6 flex flex-col gap-5">
          {error && (
            <div className="px-4 py-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.2)' }}>
              {error}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Experiment Name</label>
            <input
              className="w-full px-3 py-2 rounded-lg text-sm text-white"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              placeholder="e.g. Opening line test Q2"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>

          {/* Campaign */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Campaign (optional)</label>
              <select
                className="w-full px-3 py-2 rounded-lg text-sm text-white"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                value={campaignId}
                onChange={e => setCampaignId(e.target.value)}
              >
                <option value="">Global (no campaign)</option>
                {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Optimize For</label>
              <select
                className="w-full px-3 py-2 rounded-lg text-sm text-white"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                value={metric}
                onChange={e => setMetric(e.target.value)}
              >
                <option value="conversion_rate">Conversion Rate</option>
                <option value="answer_rate">Answer Rate</option>
                <option value="call_duration">Call Duration</option>
                <option value="opt_out_rate">Opt-out Rate</option>
              </select>
            </div>
          </div>

          {/* Min sample size */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Min Sample Size (calls per variant)</label>
            <input
              type="number"
              min={10}
              max={10000}
              className="w-full px-3 py-2 rounded-lg text-sm text-white"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              value={minSampleSize}
              onChange={e => setMinSampleSize(Number(e.target.value))}
            />
          </div>

          {/* Variants */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm text-gray-400">Variants ({variants.length}/4)</label>
              <button
                onClick={addVariant}
                disabled={variants.length >= 4}
                className="text-xs px-3 py-1 rounded-lg transition-colors"
                style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.2)' }}
              >
                + Add Variant
              </button>
            </div>

            {variants.map((v, idx) => (
              <div key={idx} className="mb-4 rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-center gap-3 mb-3">
                  <input
                    className="flex-1 px-3 py-1.5 rounded-lg text-sm text-white font-medium"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                    value={v.name}
                    onChange={e => updateVariant(idx, 'name', e.target.value)}
                  />
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <input
                      type="range"
                      min={1}
                      max={98}
                      value={v.weight}
                      onChange={e => handleWeightChange(idx, Number(e.target.value))}
                      className="w-24"
                    />
                    <span className="text-xs text-gray-400 w-8 text-right">{v.weight}%</span>
                  </div>
                  {variants.length > 2 && (
                    <button onClick={() => removeVariant(idx)} className="text-gray-500 hover:text-red-400 text-sm">✕</button>
                  )}
                </div>
                <textarea
                  className="w-full px-3 py-2 rounded-lg text-sm text-gray-200 resize-none"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', minHeight: 80 }}
                  placeholder={idx === 0 ? 'Control script — your existing script...' : 'Variant script to test...'}
                  value={v.script}
                  onChange={e => updateVariant(idx, 'script', e.target.value)}
                />
              </div>
            ))}

            {/* Weight total indicator */}
            <div className="text-xs text-gray-500 text-right">
              Total weight: {variants.reduce((s, v) => s + v.weight, 0)}% {variants.reduce((s, v) => s + v.weight, 0) !== 100 && <span style={{ color: '#f87171' }}>(must equal 100)</span>}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white transition-colors">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-5 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{ background: loading ? 'rgba(99,102,241,0.4)' : 'rgba(99,102,241,0.8)', color: '#fff' }}
          >
            {loading ? 'Launching…' : '🚀 Launch Experiment'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Experiment Card ───────────────────────────────────────────────────────────

interface ExperimentCardProps {
  exp: Experiment;
  onClick: () => void;
}

const ExperimentCard: React.FC<ExperimentCardProps> = ({ exp, onClick }) => {
  const totalCalls = Number(exp.total_calls) || 0;
  const progress = Math.min(100, (totalCalls / (exp.minSampleSize || 100)) * 100);

  return (
    <div
      onClick={onClick}
      className="rounded-xl p-5 cursor-pointer transition-all hover:scale-[1.01]"
      style={{ background: '#13131e', border: '1px solid rgba(255,255,255,0.07)' }}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-white font-semibold text-base">{exp.name}</h3>
          <div className="text-xs text-gray-500 mt-0.5">{metricLabel(exp.metric)} · {exp.variant_count || 0} variants</div>
        </div>
        <span
          className="text-xs font-semibold px-2 py-1 rounded-full capitalize"
          style={{ color: statusColor[exp.status] || '#9ca3af', background: statusBg[exp.status] || 'rgba(107,114,128,0.1)' }}
        >
          {exp.status}
        </span>
      </div>

      {/* Progress */}
      <div className="mb-2">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>{totalCalls} calls</span>
          <span>Target: {exp.minSampleSize}/variant</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${progress}%`, background: progress >= 100 ? '#10b981' : '#6366f1' }}
          />
        </div>
      </div>

      <div className="text-xs text-gray-500">{new Date(exp.createdAt).toLocaleDateString()}</div>
    </div>
  );
};

// ── Variant Result Card ────────────────────────────────────────────────────────

interface VariantCardProps {
  variant: ExperimentVariant;
  isControl: boolean;
  isWinner: boolean;
  metric: string;
}

const VariantCard: React.FC<VariantCardProps> = ({ variant, isControl, isWinner, metric }) => (
  <div
    className="rounded-xl p-5"
    style={{
      background: '#13131e',
      border: `1px solid ${isWinner ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.07)'}`,
      position: 'relative',
    }}
  >
    {isWinner && (
      <div className="absolute top-3 right-3 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(16,185,129,0.15)', color: '#10b981' }}>
        🏆 Winner
      </div>
    )}
    {isControl && !isWinner && (
      <div className="absolute top-3 right-3 text-xs text-gray-500 px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.04)' }}>
        Control
      </div>
    )}

    <h4 className="text-white font-semibold mb-3">{variant.name}</h4>

    <div className="grid grid-cols-2 gap-3 mb-3">
      <div>
        <div className="text-xs text-gray-500">Calls</div>
        <div className="text-lg font-semibold text-white">{variant.calls}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500">Answer Rate</div>
        <div className="text-lg font-semibold text-white">{variant.answerRate}%</div>
      </div>
      <div>
        <div className="text-xs text-gray-500">Conversions</div>
        <div className="text-lg font-semibold text-white">{variant.conversions}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500">Conv. Rate</div>
        <div className="text-lg font-semibold text-white">{variant.conversionRate}%</div>
      </div>
    </div>

    {!isControl && (
      <div className="flex items-center gap-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <span className="text-sm font-medium" style={{ color: variant.improvement > 0 ? '#10b981' : variant.improvement < 0 ? '#ef4444' : '#9ca3af' }}>
          {variant.improvement > 0 ? '+' : ''}{variant.improvement}% vs Control
        </span>
        {variant.significant && (
          <span className="text-xs text-green-400">✓ Significant</span>
        )}
        <span className="text-xs text-gray-500 ml-auto">p={variant.pValue}</span>
      </div>
    )}
  </div>
);

// ── Experiment Detail View ─────────────────────────────────────────────────────

interface ExperimentDetailProps {
  experimentId: string;
  onBack: () => void;
  onStatusChange: () => void;
}

const ExperimentDetail: React.FC<ExperimentDetailProps> = ({ experimentId, onBack, onStatusChange }) => {
  const [experiment, setExperiment] = useState<any>(null);
  const [results, setResults] = useState<ExperimentResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/v1/experiments/${experimentId}`, { withCredentials: true });
      setExperiment(res.data.experiment);
      setResults(res.data.results);
    } catch {}
    setLoading(false);
  }, [experimentId]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (action: string) => {
    setActionLoading(action);
    try {
      await axios.post(`${API}/api/v1/experiments/${experimentId}/${action}`, {}, { withCredentials: true });
      await load();
      onStatusChange();
    } catch {}
    setActionLoading('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading…</div>
      </div>
    );
  }

  if (!experiment) return null;

  const confidenceColor2 = confidenceColor(results?.confidence || 0);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="text-gray-400 hover:text-white transition-colors text-sm">← Back</button>
        <div className="flex-1">
          <h2 className="text-white text-xl font-semibold">{experiment.name}</h2>
          <div className="text-sm text-gray-500">{metricLabel(experiment.metric)} · Min {experiment.minSampleSize} calls/variant</div>
        </div>
        <div className="flex items-center gap-2">
          {experiment.status === 'running' && (
            <button
              onClick={() => doAction('pause')}
              disabled={!!actionLoading}
              className="px-3 py-1.5 rounded-lg text-sm transition-all"
              style={{ background: 'rgba(245,158,11,0.1)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.2)' }}
            >
              {actionLoading === 'pause' ? '…' : '⏸ Pause'}
            </button>
          )}
          {experiment.status === 'paused' && (
            <button
              onClick={() => doAction('resume')}
              disabled={!!actionLoading}
              className="px-3 py-1.5 rounded-lg text-sm transition-all"
              style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981', border: '1px solid rgba(16,185,129,0.2)' }}
            >
              {actionLoading === 'resume' ? '…' : '▶ Resume'}
            </button>
          )}
          {results?.winner && experiment.status !== 'concluded' && (
            <button
              onClick={() => doAction('conclude')}
              disabled={!!actionLoading}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold transition-all"
              style={{ background: 'rgba(99,102,241,0.8)', color: '#fff' }}
            >
              {actionLoading === 'conclude' ? '…' : '🏆 Apply Winner'}
            </button>
          )}
          {results && (
            <button
              onClick={() => exportCSV(results, experiment.name)}
              className="px-3 py-1.5 rounded-lg text-sm transition-all"
              style={{ background: 'rgba(255,255,255,0.05)', color: '#9ca3af', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              ↓ Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Recommendation banner */}
      {results?.recommendation && (
        <div
          className="px-5 py-4 rounded-xl mb-6 text-sm"
          style={{
            background: results.winner ? 'rgba(16,185,129,0.08)' : 'rgba(99,102,241,0.08)',
            border: `1px solid ${results.winner ? 'rgba(16,185,129,0.2)' : 'rgba(99,102,241,0.2)'}`,
            color: results.winner ? '#34d399' : '#a5b4fc',
          }}
        >
          💡 {results.recommendation}
        </div>
      )}

      {/* Confidence meter */}
      {results && (
        <div
          className="rounded-xl p-5 mb-6"
          style={{ background: '#13131e', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-300">Statistical Confidence</span>
            <span className="text-2xl font-bold" style={{ color: confidenceColor2 }}>{results.confidence}%</span>
          </div>
          <div className="h-3 rounded-full overflow-hidden mb-2" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${results.confidence}%`, background: confidenceColor2 }}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Target: 95% confidence</span>
            {results.confidence < 95 && (
              <span>Need more calls to reach significance</span>
            )}
            {results.confidence >= 95 && (
              <span style={{ color: '#10b981' }}>✓ Statistically significant</span>
            )}
          </div>
        </div>
      )}

      {/* Variant cards */}
      {results && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {results.variants.map((v, i) => (
            <VariantCard
              key={v.id}
              variant={v}
              isControl={i === 0}
              isWinner={v.name === results.winner}
              metric={experiment.metric}
            />
          ))}
        </div>
      )}

      {/* Script comparison */}
      {experiment.variants && experiment.variants.length >= 2 && (
        <div
          className="rounded-xl p-5"
          style={{ background: '#13131e', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <h3 className="text-white font-semibold mb-4">Script Comparison</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {experiment.variants.map((v: any, i: number) => (
              <div key={v.id}>
                <div className="text-xs font-semibold mb-2" style={{ color: i === 0 ? '#9ca3af' : '#818cf8' }}>
                  {i === 0 ? '📋 Control' : `🧬 ${v.name}`}
                </div>
                <div
                  className="p-3 rounded-lg text-sm text-gray-300 leading-relaxed whitespace-pre-wrap"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', minHeight: 80 }}
                >
                  {v.script || <span className="text-gray-600 italic">No script</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main Dashboard ─────────────────────────────────────────────────────────────

export const ABTestDashboard: React.FC = () => {
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);

  const loadExperiments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/v1/experiments`, { withCredentials: true });
      setExperiments(res.data.experiments || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    loadExperiments();
    // Load campaigns for the dropdown
    axios.get(`${API}/api/v1/campaigns`, { withCredentials: true })
      .then(res => setCampaigns(res.data.campaigns || res.data.data || []))
      .catch(() => {});
  }, [loadExperiments]);

  if (selectedId) {
    return (
      <div className="p-6">
        <ExperimentDetail
          experimentId={selectedId}
          onBack={() => setSelectedId(null)}
          onStatusChange={loadExperiments}
        />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Page header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">🧪 A/B Tests</h1>
          <p className="text-gray-500 text-sm mt-1">Test call scripts and measure which performs best</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
          style={{ background: 'rgba(99,102,241,0.8)', color: '#fff' }}
        >
          + New Experiment
        </button>
      </div>

      {/* Experiments grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48 text-gray-500">Loading…</div>
      ) : experiments.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center h-64 rounded-xl text-center"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.08)' }}
        >
          <div className="text-5xl mb-4">🧪</div>
          <div className="text-white font-semibold text-lg mb-2">No experiments yet</div>
          <div className="text-gray-500 text-sm mb-5">Create your first A/B test to start optimizing your scripts</div>
          <button
            onClick={() => setShowModal(true)}
            className="px-5 py-2 rounded-lg text-sm font-semibold"
            style={{ background: 'rgba(99,102,241,0.8)', color: '#fff' }}
          >
            + New Experiment
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {experiments.map((exp) => (
            <ExperimentCard key={exp.id} exp={exp} onClick={() => setSelectedId(exp.id)} />
          ))}
        </div>
      )}

      {/* New experiment modal */}
      {showModal && (
        <NewExperimentModal
          campaigns={campaigns}
          onClose={() => setShowModal(false)}
          onCreated={loadExperiments}
        />
      )}
    </div>
  );
};

export default ABTestDashboard;
